import React, { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { PSDialog } from 'enrollment/utils/common/Utils';
import { PsDrawerDialog } from 'stencilSetup';

export interface OrbitDrawerProps {
  drawerId: string;
  heading: string;
  isOpen: boolean;
  closeIcon?: string;
  handleClose?: () => void;
  handleSubmit?: () => void;
  awaitSuccess?: boolean;
  children?: React.ReactNode;
  footer?: boolean; // show or do not show the footer with save & / or cancel button
  saveButton?: string;
  saveButtonDisabled?: boolean;
  closeButtonLabel?: string;
  formId?: string;
  dialogStatic?: boolean;
  dialogLarge?: boolean;
  hTagImg?: string;
  hTagImgAlt?: string;
  keepMounted?: boolean;
  dialogRef?: (el: HTMLDivElement) => void;
  dataTestId?: string;
}

export const OrbitDrawer: FC<OrbitDrawerProps> = ({
  drawerId,
  heading,
  isOpen,
  closeIcon,
  handleClose,
  handleSubmit,
  awaitSuccess = false,
  footer = false,
  saveButton = 'Submit',
  saveButtonDisabled = false,
  closeButtonLabel = 'Close',
  formId,
  children,
  dialogStatic = true,
  dialogLarge = false,
  hTagImg,
  hTagImgAlt,
  keepMounted = false,
  // eslint-disable-next-line
  dataTestId = drawerId,
  dialogRef = () => undefined,
}) => {
  const [isDelayedOpen, setIsDelayedOpen] = useState(false);

  const handleSubmitDrawer = () => {
    if (handleSubmit) {
      if (!awaitSuccess) {
        PSDialog.dialogClose(drawerId, handleSubmit);
      } else {
        handleSubmit();
      }
    }
  };

  const handleCloseDrawer = () => {
    if (handleClose) {
      PSDialog.dialogClose(drawerId, handleClose);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const loadTimer = setTimeout(() => {
        if (keepMounted) {
          PSDialog.dialogOpen(drawerId);
        } else {
          setIsDelayedOpen(true);

          const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              if (mutation.type === 'childList') {
                const dialogElement = document.querySelector(
                  `dialog#${drawerId}`
                );
                if (dialogElement) {
                  PSDialog.dialogOpen(drawerId);
                  observer.disconnect();
                  break;
                }
              }
            }
          });

          observer.observe(document.body, { childList: true, subtree: true });
          return () => observer.disconnect();
        }
      }, 25);
      return () => clearTimeout(loadTimer);
    } else if (!keepMounted) {
      setIsDelayedOpen(false);
    }
  }, [drawerId, isOpen, keepMounted]);

  return isDelayedOpen || keepMounted
    ? createPortal(
        <PsDrawerDialog
          dialogId={drawerId}
          dialogHeading={heading}
          closeIcon={closeIcon}
          closeButtonMethod={handleCloseDrawer}
          submitButtonMethod={handleSubmitDrawer}
          awaitSuccess={awaitSuccess}
          dialogFooter={footer}
          dialogButtonLabel={saveButton}
          closeButtonLabel={closeButtonLabel}
          dialogButtonDisabled={saveButtonDisabled}
          formId={formId}
          dialogStatic={dialogStatic}
          dialogLarge={dialogLarge}
          hTagImg={hTagImg}
          hTagImgAlt={hTagImgAlt}
          dialogRef={dialogRef}
        >
          {children}
        </PsDrawerDialog>,
        document.body
      )
    : null;
};
